import { Link } from "react-router-dom";
import { ArrowUpRight, Film, Captions } from "lucide-react";
import { HomeHero } from "@/components/site/HomeHero";
import { Marquee } from "@/components/site/Marquee";
import { PillarBento } from "@/components/site/Pillars";
import { Container, SectionHead, CtaBand } from "@/components/site/blocks";
import { DOC_URL, DOC_EMBED } from "@/components/site/nav";
import { Reveal } from "@/components/site/motion";
import { useSeo } from "@/components/site/useSeo";

const Feature = () => (
  <section className="relative py-24 sm:py-32" data-testid="home-feature-documentary">
    <Container className="grid items-start gap-14 lg:grid-cols-12">
      <Reveal className="lg:col-span-4 lg:row-span-2">
        <figure className="group relative mx-auto max-w-[420px] [perspective:1200px]" data-testid="home-documentary-poster">
          <div aria-hidden="true" className="absolute -inset-8 rounded-full bg-[#E9A23B]/15 blur-3xl" />
          <div className="relative aspect-[2/3] overflow-hidden rounded-2xl border border-[#D4AF37]/40 shadow-[0_50px_120px_-40px_rgba(243,198,73,0.55)] transition-transform duration-700 group-hover:[transform:rotateY(-6deg)_rotateX(3deg)]">
            <img src="/images/documentary-poster.jpg" loading="lazy" className="h-full w-full object-cover"
              alt="Poster for The John Mason Story and His Journey with God: John's face in a hood blended with a sunset over the ocean. Tagline: One man's story of faith, resilience, and divine purpose. Produced and directed by John Mason. Filmed by King James Films." />
          </div>
          <figcaption className="mt-4 text-xs uppercase tracking-[0.2em] text-[#C8C5BB]">Official poster</figcaption>
        </figure>
      </Reveal>
      <Reveal className="order-last lg:order-none lg:col-span-8">
        <div className="relative">
          <div aria-hidden="true" className="absolute -inset-10 rounded-full bg-[#D4AF37]/10 blur-3xl" />
          <div className="relative aspect-video overflow-hidden rounded-2xl border border-[#D4AF37]/40 bg-[#12141C] shadow-[0_40px_120px_-40px_rgba(243,198,73,0.4)]">
            <iframe src={DOC_EMBED} title="The John Mason Story and His Journey with God — documentary video player"
              className="absolute inset-0 h-full w-full" loading="lazy" data-testid="home-documentary-player"
              allow="fullscreen; picture-in-picture; clipboard-write; encrypted-media" referrerPolicy="strict-origin-when-cross-origin" />
          </div>
          <a href={DOC_URL} target="_blank" rel="noopener noreferrer" data-testid="home-documentary-link"
            className="relative mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#F3C649] underline-offset-4 hover:underline">
            Open on Vimeo <ArrowUpRight size={16} aria-hidden="true" /><span className="sr-only"> (opens in a new tab)</span>
          </a>
        </div>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-8 lg:row-start-1 lg:col-start-5">
        <p className="label flex items-center gap-2"><Film size={14} aria-hidden="true" /> Featured documentary</p>
        <h2 className="mt-4 font-display text-4xl font-semibold leading-[1.02] sm:text-5xl">
          The John Mason Story <span className="italic text-[#F3C649]">and His Journey with God</span>
        </h2>
        <p className="mt-6 text-base leading-relaxed text-[#C8C5BB] sm:text-lg">
          John's story is the starting point. It opens honest conversation about resilience, faith, independence, and purpose.
        </p>
        <p className="mt-4 text-base leading-relaxed text-[#C8C5BB]">
          From there, participants reflect on their own path and leave with practical next steps.
        </p>
        <p className="mt-6 flex items-center gap-2 text-sm text-[#E6E3DA]"><Captions size={16} aria-hidden="true" className="text-[#F3C649]" /> Turn on captions with the CC button in the player when they're available.</p>
        <Link to="/about" className="btn-ghost mt-8" data-testid="home-about-john-btn">Meet John <ArrowUpRight size={18} aria-hidden="true" /></Link>
      </Reveal>
    </Container>
  </section>
);

const Complement = () => (
  <section className="py-20 sm:py-28" data-testid="home-complement">
    <Container>
      <Reveal>
        <p className="max-w-5xl font-display text-3xl leading-[1.15] text-[#F9F8F6] sm:text-5xl">
          JEM uses one man's real story to spark <span className="italic text-[#F3C649]">self-advocacy, independence, education,</span> and{" "}
          <span className="italic text-[#F3C649]">employment readiness</span> — and it works alongside the individualized services people already receive.
        </p>
        <p className="mt-8 max-w-xl text-base text-[#C8C5BB]">It complements existing services. It never replaces them.</p>
      </Reveal>
    </Container>
  </section>
);

const MISSION_PILLARS = [
  { w: "Journey", t: "Understand your story." },
  { w: "Empower", t: "Recognize your abilities and advocate for yourself." },
  { w: "Motivate", t: "Take the next step toward your goals." },
];

const Mission = () => (
  <section className="relative border-y border-[#D4AF37]/20 bg-[#07080A] py-24 sm:py-32" data-testid="home-mission" aria-labelledby="mission-heading">
    <Container className="grid gap-14 lg:grid-cols-12">
      <Reveal className="lg:col-span-7">
        <p className="label">Our mission</p>
        <h2 id="mission-heading" className="sr-only">Mission Statement</h2>
        <p className="mt-6 font-display text-3xl leading-[1.18] text-[#F9F8F6] sm:text-4xl" data-testid="mission-statement">
          The mission of JEM — Journey • Empower • Motivate is to inspire and equip people with disabilities to recognize their potential, develop self-confidence and self-advocacy skills, and pursue meaningful opportunities in <span className="italic text-[#F3C649]">education, employment, independent living, and community life.</span>
        </p>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#C8C5BB] sm:text-lg">
          Through lived experience, educational programming, documentary storytelling, interactive discussions, and practical goal-setting, JEM encourages participants to embrace their Journey, become Empowered by their abilities and available resources, and stay Motivated to turn their goals into action.
        </p>
      </Reveal>
      <ul className="space-y-4 lg:col-span-5 lg:pt-14" data-testid="mission-pillars">
        {MISSION_PILLARS.map((p, i) => (
          <Reveal key={p.w} delay={0.1 + i * 0.1}>
            <li className="group flex flex-col gap-1 border-b border-[#D4AF37]/25 pb-5 sm:grid sm:grid-cols-[12.5rem_1fr] sm:items-baseline sm:gap-4">
              <span className="font-display text-4xl font-semibold text-[#F3C649] transition-transform duration-500 group-hover:translate-x-2 sm:text-5xl">{p.w}</span>
              <span className="text-base text-[#E6E3DA]">— {p.t}</span>
            </li>
          </Reveal>
        ))}
      </ul>
    </Container>
  </section>
);

export default function Home() {
  useSeo("Home", "JEM is a documentary-based program by John E. Mason that turns real-life experience into conversation, confidence, and practical next steps.");
  return (
    <>
      <HomeHero />
      <Marquee />
      <Complement />
      <Mission />
      <Feature />
      <section className="py-24 sm:py-32" data-testid="home-framework">
        <Container>
          <div className="mb-14 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
            <SectionHead label="The framework" title={<>Three words. <span className="italic text-[#F3C649]">One path forward.</span></>} />
            <Reveal><Link to="/program" className="btn-ghost" data-testid="home-framework-program-btn">Explore the Program <ArrowUpRight size={18} aria-hidden="true" /></Link></Reveal>
          </div>
          <PillarBento />
        </Container>
      </section>
      <CtaBand />
    </>
  );
}
